import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { ClassItem } from '../../data/classes';
import { Button, PageHeader, Pagination, Input } from '@/components/shared/ui';
import { useOpenClassList } from '../../data/queries/useClasses';
import { useAuth } from '../../contexts/AuthContext';
import { removeClassEnrollment } from '../../utils/myClassStatus';
import type { OpenClassListItemDto } from '@/types/api/class';
import { getLocalizedClassItem, isOpenClassItem, isOpenClassListEntryEnded, openClassListItemDtoToClassItem } from '@/utils/classUtils';
import type { ScenarioListPlatformFilterKey } from '@/utils/scenarioUtils';
import { OPEN_LIST_FALLBACK_DEVICE_TYPE_IDS } from '@/utils/scenarioUtils';
import { OpenClassListCardMeta } from '@/components/class/OpenClassListCardMeta';
import { formatCurrencyForLocale } from '@/lib/currencyFormat';

const ITEMS_PER_PAGE = 8;

export default function OpenClassList(): React.ReactElement {
    const { t, i18n } = useTranslation(['class']);
    const navigate = useNavigate();
    const { user, getCurrentRole } = useAuth();
    const role = getCurrentRole();
    const isLoggedIn = user !== null;
    const basePath = role === 'admin' ? '/admin' : role === 'master' ? '/master' : '/student';

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [platformFilter, setPlatformFilterState] = useState<'all' | ScenarioListPlatformFilterKey>('all');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const id = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
        return () => window.clearTimeout(id);
    }, [searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, platformFilter]);

    const openListParams = useMemo(
        () => ({
            page: currentPage,
            pageSize: ITEMS_PER_PAGE,
            searchText: debouncedSearch.trim() || undefined,
            scenarioDeviceTypeId: platformFilter === 'all' 
                ? undefined 
                : OPEN_LIST_FALLBACK_DEVICE_TYPE_IDS[platformFilter],
        }),
        [currentPage, debouncedSearch, platformFilter]
    );

    const { data, isLoading, isError, error } = useOpenClassList(openListParams, {
        enabled: true,
    });

    const pageLoading = isLoading;

    useEffect(() => {
        const raw = localStorage.getItem('participatingClasses') || '{}';
        const participatingClasses = JSON.parse(raw) as Record<string, boolean>;
        if (participatingClasses['001']) {
            delete participatingClasses['001'];
            localStorage.setItem('participatingClasses', JSON.stringify(participatingClasses));
        }
        // 오픈클래스 필수의료 시뮬레이션(004): 참가자로 등록되어 있지 않게 유지
        removeClassEnrollment('004');
    }, []);

    const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / ITEMS_PER_PAGE));

    const currentClasses = useMemo(
        () =>
            (data?.classList ?? [])
                .filter((dto) => !isOpenClassListEntryEnded(dto as OpenClassListItemDto & Record<string, unknown>))
                .map((dto) => openClassListItemDtoToClassItem(dto)),
        [data?.classList]
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const setPlatformFilter = (value: 'all' | ScenarioListPlatformFilterKey) => {
        setPlatformFilterState(value);
        setCurrentPage(1);
    };

    if (pageLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                <p className="text-gray-500">{t('class:loading')}</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="max-w-[1280px] mx-auto px-4 py-8">
                <PageHeader title={t('class:openClassList')} description={t('class:openClassListDescription')} />
                <div className="mt-8 rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-red-800 text-sm">
                    {(error as Error)?.message || t('class:openClassListLoadError')}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-[1280px] mx-auto px-4 py-8">
            <PageHeader
                title={t('class:openClassList')}
                description={t('class:openClassListDescription')}
            />

            {/* 좌측: 플랫폼(전체·VR·모바일·PC) / 우측: 검색 */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                    {(
                        [
                            ['all', t('class:platformAll')] as const,
                            ['VR', t('class:platformVr')] as const,
                            ['MOBILE', t('class:platformMobile')] as const,
                            ['PC', t('class:platformPc')] as const,
                        ] as const
                    ).map(([value, label]) => (
                        <Button
                            key={value}
                            type="button"
                            onClick={() => setPlatformFilter(value)}
                            variant={platformFilter === value ? 'lightdark' : 'outline'}
                            size="md"
                        >
                            {label}
                        </Button>
                    ))}
                </div>

                <div className="relative w-full sm:w-72 shrink-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={18} />
                    <Input
                        type="text"
                        placeholder={t('searchClassOrgPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {currentClasses.length === 0 ? (
                    <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-500">
                        <p className="text-lg">{t('noCategoryClasses')}</p>
                    </div>
                ) : (
                    currentClasses.map((item) => {
                        const displayItem = getLocalizedClassItem(item as ClassItem, i18n.language);
                        const isEnrolled = (() => {
                            if (item.apiEnrollment === true) return true;
                            if (item.apiEnrollment === false) return false;
                            if (!isLoggedIn) return false;
                            const participatingClasses = JSON.parse(localStorage.getItem('participatingClasses') || '{}') as Record<string, boolean>;
                            try {
                                const raw = localStorage.getItem('myClassStatus') || '{}';
                                const myClassStatusMap = JSON.parse(raw) as Record<string, string>;
                                return !!participatingClasses[item.id] || !!myClassStatusMap[item.id];
                            } catch {
                                return !!participatingClasses[item.id];
                            }
                        })();

                        return (
                            <div
                                key={item.id}
                                onClick={() => {
                                    // 로그인 전: 모든 카드 클릭 시 오픈클래스 상세로 이동
                                    if (!isLoggedIn) {
                                        navigate(`/open-class/${item.id}`);
                                        return;
                                    }
                                    // 어드민: 오픈클래스 상세는 일반 페이지 레이아웃 유지(/open-class/:id)
                                    if (role === 'admin') {
                                        navigate(`/open-class/${item.id}`);
                                        return;
                                    }
                                    // 학생/마스터: 주문완료 시 마이클래스 상세, 주문전 시 오픈클래스 상세
                                    if (isEnrolled) {
                                        navigate(`${basePath}/my-classes/${item.id}`);
                                    } else {
                                        navigate(`/open-class/${item.id}`);
                                    }
                                }}
                                className="flex flex-col h-[420px] bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                            >
                                {/* Thumbnail */}
                                <div className="w-full h-64 relative overflow-hidden bg-gray-100">
                                    {displayItem.thumbnail ? (
                                        <img
                                            src={displayItem.thumbnail}
                                            alt={displayItem.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                                            No Image
                                        </div>
                                    )}
                                    {/* Badges */}
                                    {(displayItem.isNew || displayItem.isRecommended || isEnrolled || displayItem.price === 0) && (
                                        <div className="absolute top-3 left-3 flex gap-2">
                                            {isEnrolled && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-600 text-white shadow-sm">
                                                    {t('class:enrolled')}
                                                </span>
                                            )}
                                            {displayItem.isNew && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-500 text-white">
                                                    NEW
                                                </span>
                                            )}
                                            {displayItem.isRecommended && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-orange-500 text-white">
                                                    {t('class:badgeRecommended')}
                                                </span>
                                            )}
                                            {displayItem.price === 0 && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-purple-500 text-white">
                                                    {t('class:badgeFree')}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                </div>

                                {/* Content */}
                                <div className="p-4 flex flex-col flex-1">
                                    <h3 className="text-base font-bold text-gray-900 mb-3">
                                        {displayItem.title}
                                    </h3>

                                    <OpenClassListCardMeta item={displayItem} />

                                    {/* 개설기관 및 참가비 */}
                                    <div className="mt-auto pt-3">
                                        <div className="flex items-end justify-between">
                                            {/* 개설기관 */}
                                            {(displayItem.organizationLogo || displayItem.organizationName) && (
                                                <div>
                                                    {displayItem.organizationLogo ? (
                                                        <img
                                                            src={displayItem.organizationLogo}
                                                            alt={displayItem.organizationName || t('class:organizationLogoAlt')}
                                                            className="h-5 object-contain"
                                                            onError={(e) => {
                                                                e.currentTarget.style.display = 'none';
                                                                const parent = e.currentTarget.parentElement;
                                                                if (parent && displayItem.organizationName) {
                                                                    const text = document.createElement('div');
                                                                    text.className = 'text-sm text-gray-500 font-medium';
                                                                    text.textContent = displayItem.organizationName;
                                                                    parent.appendChild(text);
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="text-sm font-medium text-gray-500">
                                                            {displayItem.organizationName}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* 참가비 */}
                                            {displayItem.price !== undefined && (
                                                <span className="text-sm font-semibold text-gray-800">
                                                    {formatCurrencyForLocale(
                                                        displayItem.discountPrice !== undefined ? displayItem.discountPrice : displayItem.price,
                                                        i18n.language
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="mt-8 mb-12">
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
}
